/**
 * Общие элементы кабинета исполнителя.
 *
 * Семь экранов кабинета — это по сути семь форм, и без общего набора они
 * заведут семь слегка разных полей ввода. Расхождения такого рода замечают
 * позже всего: где-то пропадёт подпись, где-то ошибка перестанет читаться
 * скринридером, а выглядеть всё будет одинаково.
 *
 * Ошибки полей приходят с сервера в `error.fields` и подставляются сюда как
 * есть — тот же приём, что в `AddressScreen`. Ключ `_` означает ошибку всей
 * формы, а не конкретного поля.
 */
import type { CSSProperties, ReactNode } from 'react';

export const sectionTitleStyle: CSSProperties = {
  fontSize: 'var(--fs-h2)',
  lineHeight: 'var(--lh-h2)',
  margin: '0 0 var(--s-3)',
};

export const hintStyle: CSSProperties = {
  fontSize: 'var(--fs-sm)',
  lineHeight: 'var(--lh-sm)',
  color: 'var(--c-text-secondary)',
  margin: '0 0 var(--s-3)',
};

export const errorTextStyle: CSSProperties = {
  fontSize: 'var(--fs-sm)',
  lineHeight: 'var(--lh-sm)',
  color: 'var(--c-danger)',
  margin: 'var(--s-2) 0 0',
};

/**
 * Полоса действий, прижатая к низу экрана.
 *
 * `--tg-safe-bottom` обязателен: на телефонах с жестовой навигацией кнопка
 * иначе оказывается под системной полосой и не нажимается.
 */
export const bottomBarStyle: CSSProperties = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  padding: 'var(--s-3) var(--page-x)',
  paddingBottom: 'calc(var(--s-3) + var(--tg-safe-bottom))',
  background: 'var(--c-surface)',
  borderTop: '1px solid var(--c-border)',
  boxShadow: 'var(--sh-lg)',
  display: 'grid',
  gap: 'var(--s-2)',
};

/** Отступ снизу у страницы с фиксированной полосой — чтобы её не перекрывало. */
export const pageWithBottomBar: CSSProperties = { paddingBottom: 132 };

const inputBase: CSSProperties = {
  width: '100%',
  minHeight: 'var(--tap-min)',
  padding: '0 var(--s-3)',
  borderRadius: 'var(--r-md)',
  background: 'var(--c-surface)',
  color: 'var(--c-text)',
  font: 'var(--fw-regular) var(--fs-body)/var(--lh-body) var(--font)',
};

export function Field({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  children: (props: {
    id: string;
    style: CSSProperties;
    'aria-invalid': boolean | undefined;
    'aria-describedby': string | undefined;
  }) => ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          marginBottom: 'var(--s-2)',
          fontSize: 'var(--fs-sm)',
          color: 'var(--c-text-secondary)',
        }}
      >
        {label}
      </label>

      {children({
        id,
        style: {
          ...inputBase,
          border: `1px solid ${error ? 'var(--c-danger)' : 'var(--c-border-input)'}`,
        },
        'aria-invalid': error ? true : undefined,
        'aria-describedby': error ? `${id}-err` : hint ? `${id}-hint` : undefined,
      })}

      {hint && !error && (
        <p id={`${id}-hint`} style={{ ...hintStyle, margin: 'var(--s-2) 0 0' }}>
          {hint}
        </p>
      )}

      {error && (
        <p id={`${id}-err`} role="alert" style={errorTextStyle}>
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Переключатель «да/нет».
 *
 * Именно кнопка с `aria-pressed`, а не `<input type="checkbox">` со скрытой
 * разметкой: состояние читается скринридером без подсказок, а тач-цель
 * занимает всю строку — по ней трудно промахнуться.
 */
export function Toggle({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--s-3)',
          width: '100%',
          minHeight: 'var(--tap-min)',
          padding: 'var(--s-2) var(--s-3)',
          borderRadius: 'var(--r-md)',
          border: `1px solid ${checked ? 'var(--c-primary-text)' : 'var(--c-border-input)'}`,
          background: checked ? 'var(--c-primary-subtle)' : 'var(--c-surface)',
          color: 'var(--c-text)',
          font: 'var(--fw-medium) var(--fs-body)/var(--lh-body) var(--font)',
          textAlign: 'left',
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        <span>{label}</span>
        <span
          aria-hidden="true"
          style={{
            flex: '0 0 auto',
            width: 44,
            height: 26,
            borderRadius: 'var(--r-full)',
            background: checked ? 'var(--c-primary)' : 'var(--c-inactive)',
            position: 'relative',
            transition: 'background var(--dur-fast) var(--ease)',
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 3,
              left: checked ? 21 : 3,
              width: 20,
              height: 20,
              borderRadius: 'var(--r-full)',
              background: 'var(--c-surface)',
              boxShadow: 'var(--sh-sm)',
              transition: 'left var(--dur-fast) var(--ease)',
            }}
          />
        </span>
      </button>

      {hint && <p style={{ ...hintStyle, margin: 'var(--s-2) 0 0' }}>{hint}</p>}
    </div>
  );
}

/**
 * Пустое состояние. Их на экране должно быть три — «пусто», «ничего не
 * найдено» и «ошибка», — и у каждого своё действие. Общий каркас на все три,
 * чтобы разница была только в тексте.
 */
export function EmptyState({
  title,
  text,
  actionLabel,
  onAction,
  tone = 'neutral',
}: {
  title: string;
  text?: string;
  actionLabel?: string;
  onAction?: () => void;
  tone?: 'neutral' | 'error';
}) {
  return (
    <div
      style={{
        padding: 'var(--s-6) var(--s-4)',
        textAlign: 'center',
        border: '1px dashed var(--c-border)',
        borderRadius: 'var(--r-lg)',
      }}
      role={tone === 'error' ? 'alert' : undefined}
    >
      <p
        style={{
          margin: 0,
          font: 'var(--fw-medium) var(--fs-body)/var(--lh-body) var(--font)',
          color: tone === 'error' ? 'var(--c-danger)' : 'var(--c-text)',
        }}
      >
        {title}
      </p>
      {text && <p style={{ ...hintStyle, margin: 'var(--s-2) 0 0' }}>{text}</p>}
      {actionLabel && onAction && (
        <button
          type="button"
          onClick={onAction}
          style={{
            marginTop: 'var(--s-4)',
            minHeight: 'var(--tap-min)',
            padding: '0 var(--s-5)',
            borderRadius: 'var(--r-lg)',
            border: '1px solid var(--c-border-input)',
            background: 'var(--c-surface)',
            color: 'var(--c-primary-text)',
            font: 'var(--fw-medium) var(--fs-body)/var(--lh-body) var(--font)',
            cursor: 'pointer',
          }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

/** Строка-раздел на главном экране кабинета: заголовок, пояснение, стрелка. */
export function SectionRow({
  title,
  subtitle,
  warning,
  onClick,
}: {
  title: string;
  subtitle: string;
  warning?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--s-3)',
        width: '100%',
        minHeight: 'var(--tap-min)',
        padding: 'var(--s-3)',
        borderRadius: 'var(--r-lg)',
        border: '1px solid var(--c-border)',
        background: 'var(--c-surface)',
        color: 'var(--c-text)',
        font: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <span>
        <span
          style={{ display: 'block', font: 'var(--fw-medium) var(--fs-body)/var(--lh-body) var(--font)' }}
        >
          {title}
        </span>
        <span
          style={{
            display: 'block',
            marginTop: 'var(--s-1)',
            fontSize: 'var(--fs-sm)',
            // Цвет не единственный носитель смысла: текст подсказки и без него
            // говорит, чего не хватает
            color: warning ? 'var(--c-danger)' : 'var(--c-text-secondary)',
          }}
        >
          {subtitle}
        </span>
      </span>
      <span aria-hidden="true" style={{ color: 'var(--c-text-tertiary)' }}>
        ›
      </span>
    </button>
  );
}
